import { createRoute, useLoaderData, useParams } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { configuratorPageQueryOptions } from "~/app/api/pageData/configuratorPageQuery";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { ConfiguratorPageEntry } from "~/pages/ConfiguratorPage/ConfiguratorPage";

export async function loadConfiguratorPageData(
  context: { queryClient: QueryClient },
  productCode: string,
  search = "",
) {
  const result = await context.queryClient.ensureQueryData(configuratorPageQueryOptions(productCode, search));

  return unwrapPageInitialData(result);
}

function ConfiguratorRoute() {
  const result = useLoaderData({ strict: false }) as PageInitialData<void>;
  const shellProps = useStorefrontShellProps();
  const params = useParams({ strict: false });

  // The configurator renders its own full-page UI (no CMS slot structure), so it is body-only.
  // Default the templateUid: the dedicated route knows its template, and the page-data metadata
  // can arrive as the withoutResolvedPage fallback (null templateUid) at runtime.
  const metadata = {
    ...result.metadata,
    templateUid: result.metadata.templateUid ?? "ProductConfigurationTemplate",
  };

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={metadata}>
      <ConfiguratorPageEntry productCode={params.productCode} />
    </StorefrontShellFrame>
  );
}

const sharedRouteOptions = {
  getParentRoute: () => rootRoute,
  loader: async ({
    context,
    location,
    params,
  }: {
    context: { queryClient: QueryClient };
    location: { pathname: string; searchStr: string };
    params: { productCode: string };
  }) => loadConfiguratorPageData(context, params.productCode, location.searchStr),
  component: ConfiguratorRoute,
} as const;

export const configuratorRoute = createRoute({
  ...sharedRouteOptions,
  path: "/$productCode/configuratorPage/LinearConfigurator",
});

export const railsConfiguratorRoute = createRoute({
  ...sharedRouteOptions,
  path: "/rails/$productCode/railsConfiguratorPage/LinearConfigurator",
});

export const carriagesConfiguratorRoute = createRoute({
  ...sharedRouteOptions,
  path: "/carriages/$productCode/carriagesConfiguratorPage/LinearConfigurator",
});

export const shaftConfiguratorRoute = createRoute({
  ...sharedRouteOptions,
  path: "/shaft/$productCode/shaftConfiguratorPage/LinearConfigurator",
});
